import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { ArrowRightSolid, CalendarSolid, Link, Stack, Text } from '@bonusly/design-system';

import { useTrialStatus } from '../../../../hooks/use-trial-status';
import { useRecognizeURL } from '../../../../lib/url/hooks/use-recognize-url';

export function TrialBanner() {
	const keyPrefix = 'trial_banner';
	const { t } = useTranslation('vertical_navbar', { keyPrefix });
	const { track } = useAnalytics();
	const billingURL = useRecognizeURL('/company/billing');

	// Trial timing + eligibility come from the shared hook so the banner, the
	// user-navbar trial card, and the interstitial gate stay in lockstep.
	const { daysLeft, isPostExpiry, isInScope, canSeeWhatsNext } = useTrialStatus();

	// `isInScope` already excludes superadmin-initialized companies and out-of-
	// window lapsed trials, so there's nothing left to nudge when it's false.
	if (!isInScope) return;

	// `count` (days left) is only rendered on the in-trial path, where `daysLeft`
	// is non-negative; the post-expiry path shows `trial_ended` instead. A
	// negative `daysLeft` here means the trial lapsed within the post-expiry
	// window (`isInScope` already drops out-of-window lapses), so we keep
	// rendering — the banner switches to the post-expiry copy below.
	const count = daysLeft ?? 0;

	return (
		<Stack alignItems="center" bgColor={isPostExpiry ? 'error.datavizMid' : 'success.datavizMid'} gap={3} m={1} mb={2} mt={3} p={4} rounded="sm">
			<Text color="text.onColor" size="sm" textAlign="center">
				<CalendarSolid position="relative" size="xs" top={0.5} />{' '}
				{isPostExpiry ? (
					<strong>{t('trial_ended')}</strong>
				) : (
					<>
						<strong>{t('days', { count })}</strong> {t('left_in_trial')}
					</>
				)}
			</Text>
			<Link
				{...(canSeeWhatsNext ? { as: RouterLink, to: '/trial/whats-next' } : { href: billingURL })}
				alignItems="center"
				display="flex"
				color="text.onColor"
				fontSize="sm"
				fontWeight="medium"
				gap={2}
				onClick={() => track(AnalyticsEvent.OnboardingTrialBanner.ManagePlanClicked)}
				_hover={{ textDecor: 'none' }}
			>
				{isPostExpiry ? t('choose_plan') : t('manage_plan')} <ArrowRightSolid size="xs" />
			</Link>
		</Stack>
	);
}
